import { ReactNode } from 'react'
import styled from '@emotion/styled'
import { DESIGN_TOKENS } from 'constants/color.constants'
import { CircularProgress } from '@mui/material'

const emptyFunc = () => undefined

export const BUTTON_COLORS = {
    primary: DESIGN_TOKENS.text.background,
    secondary: DESIGN_TOKENS.text.foreground,
    ghost: DESIGN_TOKENS.text.foreground,
    destructive: DESIGN_TOKENS.text.errorLight,
    confirm: DESIGN_TOKENS.text.successLight,
}

export const StyledButton = styled.button<{
    isFullWidth?: boolean
    size?: keyof typeof BUTTON_SIZE_STYLES
}>`
    width: ${(p) => (p.isFullWidth ? '100%' : 'fit-content')};
    margin: 0;
    padding-top: 0;
    padding-bottom: 0;

    ${(p) => BUTTON_SIZE_STYLES[p.size || 'base']}

    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 0.6rem;
    border-radius: 0.6rem;

    font-family: Inter;
    font-size: 1.4rem;
    font-style: normal;
    font-weight: 500;
    line-height: 2.4rem;

    transition: ${DESIGN_TOKENS.transition.inputHover};

    cursor: pointer;

    &:disabled {
        cursor: default;
        opacity: 0.5;
    }

    &:focus-visible {
        outline: none;
        box-shadow: ${DESIGN_TOKENS.boxShadow.inputFocus};
    }
`

const StyledPrimaryButton = styled(StyledButton)`
    color: ${BUTTON_COLORS.primary};
    background-color: ${DESIGN_TOKENS.background.light};
    border: none;

    &:hover:not(:disabled) {
        background-color: ${DESIGN_TOKENS.hover.primary};
    }
`

const StyledSecondaryButton = styled(StyledButton)`
    color: ${BUTTON_COLORS.secondary};
    background-color: ${DESIGN_TOKENS.background.primary};
    border: 1px solid ${DESIGN_TOKENS.border.input};

    &:hover:not(:disabled) {
        background-color: ${DESIGN_TOKENS.hover.secondary};
    }
`

const StyledDestructiveButton = styled(StyledButton)`
    color: ${BUTTON_COLORS.destructive};
    background-color: ${DESIGN_TOKENS.background.error};
    border: none;

    &:hover:not(:disabled) {
        background-color: ${DESIGN_TOKENS.hover.destructive};
    }
`

const StyledGhostButton = styled(StyledButton)`
    color: ${BUTTON_COLORS.ghost};
    background-color: transparent;
    border: none;

    &:hover:not(:disabled) {
        background-color: ${DESIGN_TOKENS.hover.secondary};
    }
`

const StyledConfirmButton = styled(StyledButton)`
    color: ${BUTTON_COLORS.confirm};
    background-color: ${DESIGN_TOKENS.background.success};
    border: none;

    &:hover:not(:disabled) {
        background-color: ${DESIGN_TOKENS.hover.confirm};
    }
`

const BUTTON_VARIANTS = {
    primary: StyledPrimaryButton,
    secondary: StyledSecondaryButton,
    ghost: StyledGhostButton,
    destructive: StyledDestructiveButton,
    confirm: StyledConfirmButton,
}

const BUTTON_SIZE_STYLES = {
    sm: `
        height: 3.2rem;
        padding-left: 1rem;
        padding-right: 1rem;
    `,
    base: `
        height: 4rem;
        padding-left: 1.2rem;
        padding-right: 1.2rem;
    `,
}

export type ButtonVariant = keyof typeof BUTTON_VARIANTS

type ButtonProps = {
    isDisabled?: boolean
    isFullWidth?: boolean
    isSubmitting?: boolean
    size?: keyof typeof BUTTON_SIZE_STYLES
    variant: ButtonVariant
    className?: string
    children: ReactNode
    onClick?: () => void
    onTouchStart?: () => void
}

const Button = ({
    isDisabled = false,
    isFullWidth = false,
    isSubmitting = false,
    size = 'base',
    variant,
    className,
    children,
    onClick = emptyFunc,
    onTouchStart = emptyFunc,
}: ButtonProps) => {
    const Component = BUTTON_VARIANTS[variant]

    return (
        <Component
            disabled={isDisabled || isSubmitting}
            isFullWidth={isFullWidth}
            size={size}
            className={className}
            onClick={onClick}
            onTouchStart={onTouchStart}
        >
            {children}
            {isSubmitting && (
                <CircularProgress
                    size={16}
                    style={{ color: BUTTON_COLORS[variant] }}
                />
            )}
        </Component>
    )
}

export default Button
